/**
 * Number formatting utilities for salary/currency display.
 * Uses NZD formatting with tabular-nums for aligned columns.
 */

const currencyFormatter = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const currencyFormatterCents = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const numberFormatter = new Intl.NumberFormat("en-NZ", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/**
 * Format a number as NZD currency ($85,000)
 * @param {number} value
 * @param {boolean} [showCents=false] - Whether to show cents
 * @returns {string}
 */
export function formatCurrency(value, showCents = false) {
  if (value == null || isNaN(value)) return "$0"
  const formatter = showCents ? currencyFormatterCents : currencyFormatter
  return formatter.format(value)
}

/**
 * Format a number with commas (85,000)
 * @param {number} value
 * @returns {string}
 */
export function formatNumber(value) {
  if (value == null || isNaN(value)) return "0"
  return numberFormatter.format(value)
}

/**
 * Format a percentage (20.3%)
 * @param {number} value
 * @param {number} [decimals=1]
 * @returns {string}
 */
export function formatPercent(value, decimals = 1) {
  if (value == null || isNaN(value)) return "0%"
  return `${Number(value).toFixed(decimals)}%`
}

/**
 * Parse a formatted currency/number string back to a number.
 * Strips $, commas, spaces.
 * @param {string} str
 * @returns {number}
 */
export function parseNumber(str) {
  if (str == null) return 0
  const cleaned = String(str).replace(/[$,\s]/g, "")
  const num = parseFloat(cleaned)
  return isNaN(num) ? 0 : num
}

/**
 * Auto-format an input value as the user types.
 * Returns the formatted string and the numeric value.
 * @param {string} rawValue
 * @returns {{ formatted: string, numeric: number }}
 */
export function formatInput(rawValue) {
  const numeric = parseNumber(rawValue)
  if (numeric === 0 && rawValue.trim() === "") {
    return { formatted: "", numeric: 0 }
  }
  return { formatted: formatNumber(numeric), numeric }
}
